import { useCallback, useMemo, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { CheckoutTender, Money, CheckoutTenderCreditCustomer } from '@open-tender/types'
import { checkAmountRemaining, updateTenders } from '@open-tender/utils'
import {
  selectBrand,
  selectCheckout,
  selectContent,
  selectCustomer,
  updateForm,
  useAppDispatch,
  useAppSelector,
  resetErrors,
} from '@open-tender/cloud'
import { FormError } from 'components'
import CheckoutSection from './CheckoutSection'
import CheckoutCreditCards from './CheckoutCreditCards'
import CheckoutHouseAccounts from './CheckoutHouseAccounts'
import CheckoutGuestCreditCard from './CheckoutGuestCreditCard'
import CheckoutPay from './CheckoutPay'
import CheckoutCreditCardAdd from './CheckoutCreditCardAdd'
import { makeTenderErrors } from './Checkout.utils'

const CheckoutTendersView = styled.div`
  margin: 1.5rem 0 0;
`

const CheckoutTendersErrors = styled.div`
  margin: 0 0 1.5rem;
`

const CheckoutTenders = () => {
  const dispatch = useAppDispatch()
  const brandConfig = useAppSelector(selectBrand)
  const require_cvv_for_saved_cards: boolean = brandConfig?.require_cvv_for_saved_cards || false
  const [hasTender, setHasTender] = useState(false)
  const [cvvAddedNewCard, setCvvAddedNewCard] = useState<string>('')
  const checkout_credit_card_method = brandConfig?.checkout_credit_card_method
  const { checkout: config } = useAppSelector(selectContent) || {}
  const { auth } = useAppSelector(selectCustomer)
  const { check, form, errors } = useAppSelector(selectCheckout)
  const total = check?.totals ? check.totals.total : '0.00'
  const deposit = check?.deposit ? check.deposit : null
  const amountDue = deposit || total
  const amount = checkAmountRemaining(amountDue, form.tenders).toFixed(
    2
  ) as Money
  const isPaid = amount === '0.00'
  const tenderErrors = makeTenderErrors(
    errors.tenders as Record<string, string | Record<string, string>>
  )
  const hasErrors = tenderErrors.length > 0
  const giftTenders = useMemo(
    () => form.tenders.filter((i) => i.tender_type === 'GIFT_CARD'),
    [form.tenders]
  )
  const cards = useMemo(
    () => (check?.customer ? check.customer.credit_cards : []),
    [check?.customer]
  )
  const hasCards = cards && cards.length > 0
  const noTender = form.tenders.length === 0
  const customerCard = useMemo(
    () => (hasCards && noTender ? cards[0] : null),
    [hasCards, noTender, cards]
  )

  const [incorrectCvvError, setIncorrectCvvError] = useState<string | null>(null)

  const resetIncorrectCvvError = useCallback(() => {
    setIncorrectCvvError(null)
    if (Object.keys(errors || {}).length > 0 && 
      (errors.form || '').toString().toLowerCase().includes('there are one or more errors below')) {
      dispatch(resetErrors())
    }
  }, [dispatch, errors])

  // useEffect(() => {
  //   return () => dispatch(updateForm({ tenders: [] }))
  // }, [dispatch])

  useEffect(() => {
    const tenders = updateTenders(
      form.tenders,
      amountDue as Money
    ) as CheckoutTender[]
    if (tenders) {
      dispatch(updateForm({ tenders }))
    }
  }, [form.tenders, amountDue, dispatch])

  // automatically apply first credit card if user has one
  useEffect(() => {
    setHasTender(true)
    if (customerCard && !hasTender) {
      // shouldn't automatically apply if require_cvv_for_saved_cards enabled
      if (require_cvv_for_saved_cards) return

      const tender = { tender_type: 'CREDIT', amount, ...customerCard } as const
      dispatch(updateForm({ tenders: [tender] }))
    }
  }, [hasTender, customerCard, amount, dispatch, require_cvv_for_saved_cards])

  useEffect(() => {
    if (hasTender && require_cvv_for_saved_cards && hasErrors) {
      const cvvIncorrect = tenderErrors.find((errMsg) => errMsg.toLowerCase().includes('security code does not match'))
      // deselect the card
      if (cvvIncorrect) {
        setIncorrectCvvError(cvvIncorrect)
        dispatch(updateForm({ tenders: [] }))
      }
    }
  }, [hasTender, require_cvv_for_saved_cards, hasErrors, tenderErrors, dispatch])

  const apply = useCallback(
    (data: Omit<CheckoutTender, 'tender_type' | 'amount'>, swap?: boolean) => {
      if (parseFloat(amount) > 0 || swap) {
        const tender = {
          tender_type: 'CREDIT',
          amount,
          ...data,
          token: null,
        } as const
        dispatch(updateForm({ tenders: [...giftTenders, tender] }))
      }
    },
    [dispatch, amount, giftTenders]
  )

  const setCvvCard = useCallback(
    (cvv: number | string, data?: Omit<CheckoutTenderCreditCustomer, 'tender_type' | 'amount'>) => {
      let card = null
      const existing = form.tenders.find(
        (tender) => tender.tender_type === 'CREDIT'
      )
      if (existing) {
        card = existing
      } else if (data) {
        // new card
        card = {
          tender_type: 'CREDIT',
          amount,
          ...data,
          token: null,
        }
      }

      if (card) {
        const tender = {
          ...card,
          cvv: `${cvv}`,
        } as CheckoutTenderCreditCustomer
        dispatch(updateForm({ tenders: [...giftTenders, tender] }))
      }
    },
    [dispatch, amount, form.tenders, giftTenders]
  )

  const remove = () => {
    dispatch(updateForm({ tenders: giftTenders }))
  }
  const showCreditCards =
    checkout_credit_card_method === 'REQUIRE_ADD' ||
    checkout_credit_card_method === 'ALLOW_BOTH'

  return (
    <CheckoutSection title={config?.tenders.title}>
      <CheckoutTendersView>
        {hasErrors && (
          <CheckoutTendersErrors>
            {auth ? (
              tenderErrors.map((errMsg, index) => (
                <FormError key={`${index}-${errMsg}`} errMsg={errMsg} />
              ))
            ) : (
              <FormError errMsg="There are one or more errors below" />
            )}
          </CheckoutTendersErrors>
        )}
        {incorrectCvvError && (
          <CheckoutTendersErrors>
            <FormError key={`incorrect-cvv-error`} errMsg={incorrectCvvError} />
          </CheckoutTendersErrors>
        )}
        <CheckoutPay />
        {auth ? (
          <>
            {showCreditCards ? (
              <>
                <CheckoutCreditCards
                  apply={apply}
                  remove={remove}
                  isPaid={isPaid}
                  setCvvCard={setCvvCard}
                  cvvAddedNewCard={cvvAddedNewCard as string}
                  resetIncorrectCvvError={resetIncorrectCvvError}
                />
                <CheckoutCreditCardAdd isPaid={isPaid} setCvv={setCvvAddedNewCard} />
                <CheckoutHouseAccounts
                  apply={apply}
                  remove={remove}
                  isPaid={isPaid}
                />
                {checkout_credit_card_method === 'ALLOW_BOTH' && (
                  <CheckoutSection title="Use a different card (card will not be saved)">
                    <CheckoutGuestCreditCard />
                  </CheckoutSection>
                )}
              </>
            ) : (
              <CheckoutGuestCreditCard />
            )}
          </>
        ) : (
          <CheckoutGuestCreditCard />
        )}
      </CheckoutTendersView>
    </CheckoutSection>
  )
}

export default CheckoutTenders
